import { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  CreateEmailsError,
  useCreateEnterpriseInvitations,
} from 'src/api/queries/enterpriseInvitations';
import { Button, Form, NotificationBox } from 'src/components';
import { validateEmail } from 'src/utils/validateEmail';

import { TestId } from 'src/testids';
import type { ValidationError } from './InputMultiple';
import { InputMultiple, validateEmails } from './InputMultiple';

const inputEmailsFormKey = 'emails';
interface InviteByEmailForm {
  [inputEmailsFormKey]: { email: string }[];
}

export const InviteToEnterpriseByEmail = () => {
  const createEnterpriseInvitationsByEmail = useCreateEnterpriseInvitations();

  const form = useForm<InviteByEmailForm>({
    defaultValues: {
      [inputEmailsFormKey]: [],
    },
  });

  const {
    formState: { isSubmitted, isSubmitSuccessful },
  } = form;

  const emails = form.watch(inputEmailsFormKey);
  const numberOfEmails = emails?.length ?? 0;

  const onSubmit = (data: InviteByEmailForm) => {
    if (createEnterpriseInvitationsByEmail.isPending) {
      return;
    }

    // Remove success notification if the form has already been submitted
    if (createEnterpriseInvitationsByEmail.isSuccess) {
      createEnterpriseInvitationsByEmail.reset();
    }

    return createEnterpriseInvitationsByEmail.mutate(
      {
        emails: data.emails?.map((item) => item.email) ?? [],
      },
      {
        onError(error) {
          if (error instanceof CreateEmailsError) {
            // Reset the form with the emails that failed, so that the user can
            // try to resend them.
            form.reset({
              [inputEmailsFormKey]: error.result.unsuccessful.map((item) => ({
                email: item.email,
              })),
            });
          }
        },
      }
    );
  };

  useEffect(() => {
    if (
      isSubmitted &&
      isSubmitSuccessful &&
      createEnterpriseInvitationsByEmail.isSuccess
    ) {
      // Reset the form if the submission was successful.
      form.reset({ [inputEmailsFormKey]: [] });
    }
  }, [
    isSubmitted,
    isSubmitSuccessful,
    createEnterpriseInvitationsByEmail.isSuccess,
  ]);

  const emailsSubmittedCount =
    createEnterpriseInvitationsByEmail.variables?.emails.length ?? 0;

  const failedEmails =
    createEnterpriseInvitationsByEmail.error instanceof CreateEmailsError
      ? createEnterpriseInvitationsByEmail.error.result.unsuccessful.map(
          (item) => item.email
        )
      : [];

  return (
    <div className="text-left" data-cy={TestId.FormInviteToEnterpriseByEmail}>
      <NotificationBox
        variant="info"
        muted
        title="Only users with an existing reMarkable account can be invited. Make sure they also have access to reMarkable in your identity provider."
      ></NotificationBox>

      <Form
        onSubmit={onSubmit}
        onInvalid={() => {
          // Remove query success and error states if the user interacts with
          // the form again.
          if (
            createEnterpriseInvitationsByEmail.isSuccess ||
            createEnterpriseInvitationsByEmail.isError
          ) {
            createEnterpriseInvitationsByEmail.reset();
          }
        }}
        {...form}
      >
        <Controller
          name={inputEmailsFormKey}
          control={form.control}
          rules={{
            required: 'Please enter at least one email address',
            validate: (value) => {
              const hasInvalidEmail =
                value.findIndex((item) => {
                  return validateEmail(item.email) !== true;
                }) !== -1;

              if (hasInvalidEmail) {
                return 'One or more email addresses are invalid.';
              }

              return true;
            },
          }}
          render={({ field, fieldState }) => {
            const mappedValues = field.value.map((item) => item.email);
            const globalError: ValidationError | null = fieldState.error
              ?.message
              ? {
                  message: fieldState.error.message,
                  type: 'error',
                }
              : null;
            const errors = fieldState.error
              ? [globalError, ...validateEmails(mappedValues)]
              : undefined;

            return (
              <InputMultiple
                value={mappedValues}
                errors={errors}
                placeholder="Enter email addresses"
                onValueChange={(value) => {
                  field.onChange(value.map((email) => ({ email })));
                }}
                onBlur={field.onBlur}
              />
            );
          }}
        />

        {createEnterpriseInvitationsByEmail.isError &&
          (emailsSubmittedCount === failedEmails.length ? (
            <NotificationBox
              data-cy="email-invitations-error"
              variant="error"
              error={createEnterpriseInvitationsByEmail.error}
              className="my-16"
              title="Something went wrong"
            >
              Please check that the addresses are correct and make sure that
              they have existing reMarkable accounts before resending the
              invitations.
            </NotificationBox>
          ) : (
            <NotificationBox
              data-cy="email-invitations-warning"
              variant="warning"
              className="my-16"
              title={
                failedEmails.length <= 1 && emailsSubmittedCount === 1
                  ? "Couldn't send invitation"
                  : `Couldn't send ${failedEmails.length} of ${emailsSubmittedCount} invitations`
              }
              error={createEnterpriseInvitationsByEmail.error}
            >
              Please check that the addresses are correct and make sure that
              they have existing reMarkable accounts before resending the
              invitations.
            </NotificationBox>
          ))}

        {createEnterpriseInvitationsByEmail.isSuccess && (
          <NotificationBox
            data-cy="email-invitations-sent-notification"
            variant="success"
            className="my-16"
            title={`Invitations sent`}
          />
        )}

        <div className="flex justify-center">
          <Button
            data-cy="send-invitations-button"
            type="submit"
            size="large"
            disabled={createEnterpriseInvitationsByEmail.isPending}
            loading={createEnterpriseInvitationsByEmail.isPending}
            analytics={{
              text: 'Send invitation',
              location: ComponentLocations.MANAGE_CONNECT.MEMBERS_PAGE,
              action: 'send invitation',
            }}
          >
            {numberOfEmails > 1
              ? `Send ${numberOfEmails} invitations`
              : 'Send invitation'}
          </Button>
        </div>
      </Form>
    </div>
  );
};
